import React from "react";
import { Box, Chip, LinearProgress, Paper, Typography } from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import AddIcon from "@mui/icons-material/Add";
import { PROGRAM_COLORS } from "../programLensConstants";
import ConditionCareNotes from "./ConditionCareNotes";

const C = PROGRAM_COLORS.RPM;
const COMPLIANCE_TARGET = 16;

function RpmSummaryCard({
  patientSummary,
  apiBase,
  patientId,
  onShowSnackbar,
  onAddVitals,
  onAddCondition,
}) {
  const readings = patientSummary?.rpm_readings || [];
  const devices = patientSummary?.rpm_device_orders || [];
  const medications = patientSummary?.medications || [];

  const recentReadings = readings.slice(0, 5);
  const readingsThisMonth = readings.filter((r) => {
    const d = new Date(r.reading_date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const compliancePct = Math.min(Math.round((readingsThisMonth / COMPLIANCE_TARGET) * 100), 100);
  const remaining = Math.max(COMPLIANCE_TARGET - readingsThisMonth, 0);

  const alertColor = (level) => {
    if (level === "critical") return "#ef4444";
    if (level === "high") return "#fbbf24";
    if (level === "low") return "#fb923c";
    return "#4ade80";
  };

  const fmtReading = (r) => {
    if (r.reading_type === "blood_pressure" && r.systolic) return `BP: ${r.systolic}/${r.diastolic}`;
    if (r.reading_type === "heart_rate") return `Pulse: ${r.value} bpm`;
    if (r.reading_type === "pulse_ox") return `SpO2: ${r.value}%`;
    if (r.reading_type === "weight") return `Weight: ${r.value} lbs`;
    if (r.reading_type === "glucose") return `Glucose: ${r.value} mg/dL`;
    if (r.reading_type === "temperature") return `Temp: ${r.value}°F`;
    return `${r.reading_type}: ${r.value}`;
  };

  const fmtTimeAgo = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 1) return "just now";
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <Paper
      sx={{
        border: `2px solid ${C.main}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: `${C.main}06`,
      }}
    >
      <Box
        sx={{
          bgcolor: `${C.main}18`,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${C.main}25`,
        }}
      >
        <DevicesIcon sx={{ fontSize: 18, color: C.main }} />
        <Typography sx={{ color: C.main, fontWeight: 700, fontSize: "0.85rem", letterSpacing: 0.5 }}>
          RPM SUMMARY
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", ml: "auto" }}>
          Remote Patient Monitoring
        </Typography>
      </Box>

      <Box sx={{ p: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        {/* Recent Device Readings */}
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>
              RECENT DEVICE READINGS
            </Typography>
            {onAddVitals && (
              <Chip
                icon={<AddIcon sx={{ fontSize: 12 }} />}
                label="Add RPM Reading"
                size="small"
                onClick={onAddVitals}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem", color: C.main, borderColor: C.main, "& .MuiChip-icon": { color: C.main } }}
              />
            )}
          </Box>
          {recentReadings.length === 0 && (
            <Typography variant="caption" color="text.secondary">No readings</Typography>
          )}
          {recentReadings.map((r) => (
            <Box
              key={r.id}
              sx={{ px: 1, py: 0.75, bgcolor: `${C.main}08`, borderRadius: 1, mb: 0.5 }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: "0.8rem" }}>{fmtReading(r)}</Typography>
                <Chip
                  label={r.alert_level || "normal"}
                  size="small"
                  sx={{ height: 18, fontSize: "0.6rem", bgcolor: `${alertColor(r.alert_level)}20`, color: alertColor(r.alert_level) }}
                />
              </Box>
              <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
                {r.device_id ? `via ${r.device_id}` : "manual"} — {fmtTimeAgo(r.reading_date)}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Device Status + Compliance */}
        <Box>
          <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5, mb: 0.75 }}>
            DEVICE STATUS
          </Typography>
          {devices.length === 0 && (
            <Typography variant="caption" color="text.secondary">No devices</Typography>
          )}
          {devices.map((d) => (
            <Typography key={d.id} sx={{ fontSize: "0.8rem", mb: 0.25 }}>
              {d.device_type}{" "}
              <Chip
                label={d.status}
                size="small"
                sx={{
                  height: 16,
                  fontSize: "0.55rem",
                  bgcolor: d.status === "delivered" || d.status === "activated" ? "rgba(74,222,128,0.15)" : "rgba(251,191,36,0.15)",
                  color: d.status === "delivered" || d.status === "activated" ? "#4ade80" : "#fbbf24",
                }}
              />
            </Typography>
          ))}

          <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5, mb: 0.5, mt: 2 }}>
            READING COMPLIANCE
          </Typography>
          <Typography sx={{ fontSize: "0.8rem" }}>
            {readingsThisMonth} of {COMPLIANCE_TARGET} readings this month
          </Typography>
          <LinearProgress
            variant="determinate"
            value={compliancePct}
            sx={{
              height: 8,
              borderRadius: 4,
              mt: 0.5,
              bgcolor: "rgba(255,255,255,0.08)",
              "& .MuiLinearProgress-bar": {
                bgcolor: C.main,
                borderRadius: 4,
              },
            }}
          />
          <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", mt: 0.25 }}>
            {compliancePct}% — {remaining > 0 ? `${remaining} more needed for billing threshold` : "target met!"}
          </Typography>
        </Box>

        {/* Conditions with care notes */}
        <ConditionCareNotes
          problems={patientSummary?.problems}
          patientId={patientId}
          apiBase={apiBase}
          accentColor={C}
          onShowSnackbar={onShowSnackbar}
          onAddCondition={onAddCondition}
        />

        {/* Key Medications */}
        <Box>
          <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5, mb: 0.75 }}>
            KEY MEDICATIONS
          </Typography>
          {medications.slice(0, 5).map((m, i) => (
            <Typography key={i} sx={{ fontSize: "0.8rem" }}>
              {m.drug || m.name}{" "}
              <Box component="span" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                {m.dose} {m.frequency}
              </Box>
            </Typography>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

export default RpmSummaryCard;
